import { useCallback, useContext } from 'react';

import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { TokenContext } from '@/Contexts/TokenContext';
import { SignatureQuestionResponse } from '@/models/Question';
import { baseUrl } from '@/utils/constants';

import { useCustomFetch } from './useCustomFetch';
import usePreviewAnswer from './usePreviewAnswer';

export default function useSignatures() {
  const { authResult } = useContext(TokenContext);
  const { customFetch } = useCustomFetch();
  const { isPreviewMode, currentApplication, answers, updateAnswers } =
    useContext(CurrentApplicationContext);

  const { createPreviewSignatureResponse } = usePreviewAnswer({
    isPreviewMode,
    fetchedAnswers: answers,
    fetchedQuestions: null,
    fetchedApplicationId: currentApplication.id,
    updateAnswers,
  });
  const getSignatures = useCallback(
    async (appId: string | number | null): Promise<SignatureQuestionResponse[]> => {
      if (appId == null) {
        return [];
      }

      const url = `${baseUrl}signature?applicationid=${appId}`;
      const response = await customFetch<SignatureQuestionResponse[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  const deleteSignatureRelationship = useCallback(
    async (signatureRelationshipId: number) => {
      if (isPreviewMode) {
        return 'preview-signature-relationship-deleted';
      }

      const url = `${baseUrl}signature/deleteSignatureRelationship/${signatureRelationshipId}`;
      const response = await customFetch(url, {
        method: 'DELETE',
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${authResult.accessToken}`,
          'Cache-Control': 'no-cache',
        },
      });
      const results = await response;

      return results;
    },
    [authResult, customFetch, isPreviewMode]
  );

  const postSignature = useCallback(
    async (signatureInfo: SignatureQuestionResponse): Promise<SignatureQuestionResponse> => {
      if (isPreviewMode) {
        return createPreviewSignatureResponse(signatureInfo) as SignatureQuestionResponse;
      }

      const url = `${baseUrl}signature`;

      const response = await customFetch<SignatureQuestionResponse>(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(signatureInfo),
      });

      return response;
    },
    [authResult, createPreviewSignatureResponse, customFetch, isPreviewMode]
  );

  return {
    getSignatures,
    deleteSignatureRelationship,
    postSignature,
  };
}
